import type { WorkerToTabMessage } from "@/app/shared/services/realtime/realtime-worker";
import { Channel } from "../types";
import { BaseChannel } from "./base-channel";

enum WhatsAppEvent {
  MessageCreated = "WHATSAPP.MESSAGE_CREATED",
}

interface WhatsAppEventPayload {
  /** Encoded id of the WhatsApp link (customer_whatsapp_chat row) the message arrived in. */
  channel_id: string;
}

type WhatsAppMessageListener = () => void;

/**
 * Realtime channel for the WhatsApp tab. Joins the room of the resolved link
 * (channel_id) and notifies its listeners when a new message arrives, so the tab
 * can refetch the conversation.
 */
export class WhatsAppChannel extends BaseChannel {
  readonly channelName = Channel.WHATSAPP;

  private readonly messageListeners = new Set<WhatsAppMessageListener>();
  private joinedChannelId: string | null = null;

  constructor() {
    super();

    this.port.addEventListener("message", (event: MessageEvent<WorkerToTabMessage>) => {
      const msg = event.data;
      if (msg.type !== "event" || msg.eventName !== WhatsAppEvent.MessageCreated) {
        return;
      }

      // The SharedWorker socket is shared across tabs; only react to events of the
      // link this tab actually joined.
      const payload = msg.payload as WhatsAppEventPayload | undefined;
      if (payload?.channel_id && payload.channel_id === this.joinedChannelId) {
        this.messageListeners.forEach(listener => listener());
      }
    });
  }

  public async join(channelId: string): Promise<void> {
    if (this.joinedChannelId === channelId) {
      return;
    }

    await this.leave();
    this.joinedChannelId = channelId;
    await this.joinChannel(channelId);
  }

  public async leave(): Promise<void> {
    if (!this.joinedChannelId) {
      return;
    }

    await this.leaveChannel(this.joinedChannelId);
    this.joinedChannelId = null;
  }

  /** Subscribe to new-message events. Returns an unsubscribe function. */
  public subscribe(listener: WhatsAppMessageListener): () => void {
    this.messageListeners.add(listener);
    return () => {
      this.messageListeners.delete(listener);
    };
  }

  public async destroy(): Promise<void> {
    await this.leave();
    this.messageListeners.clear();
    this.close();
  }
}
